"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { T } from "@/i18n"
import { X, Plus } from "lucide-react"
import { FIELD_VALUE_OPTIONS, ENTITY_FIELDS } from "@/models/trigger"

type Condition = {
  id: string
  field: string
  currentValue: string | undefined
  beforeValue: string  | undefined
}

type ConditionBuilderProps = {
  entityType: string
  conditions: Condition[]
  onChange: (conditions: Condition[]) => void
}

export function ConditionBuilder({ entityType, conditions, onChange }: ConditionBuilderProps) {
  const [availableFields, setAvailableFields] = useState<{ name: string; label: string }[]>([])
  const [newCondition, setNewCondition] = useState<{
    field: string
    currentValue: string | undefined
    beforeValue: string | undefined
  }>({
    field: "",
    currentValue: undefined,
    beforeValue: undefined
  })

  // Set available fields based on entity type
  useEffect(() => {
    if (entityType) {
      setAvailableFields(ENTITY_FIELDS[entityType as keyof typeof ENTITY_FIELDS] || [])

      // Reset the new condition when entity type changes
      setNewCondition({
        field: "",
        currentValue: undefined,
        beforeValue: undefined
      })
    }
  }, [entityType])

  // Update field when available fields change
  useEffect(() => {
    if (availableFields.length > 0 && !newCondition.field) {
      setNewCondition((prev) => ({
        ...prev,
        field: availableFields[0].name,
      }))
    }
  }, [availableFields, newCondition.field])

  const addCondition = () => {
    if (newCondition.field && newCondition.currentValue) {
      const newId = Date.now().toString()
      onChange([
        ...conditions,
        {
          id: newId,
          field: newCondition.field,
          beforeValue: newCondition.beforeValue,
          currentValue: newCondition.currentValue
        },
      ])

      // Reset value but keep the field and operator
      setNewCondition((prev) => ({
        ...prev,
        beforeValue: "",
        currentValue: ""
      }))
    }
  }

  const removeCondition = (id: string) => {
    const updatedConditions = conditions.filter((condition) => condition.id !== id)
    onChange(updatedConditions)
  }

  // Check if a field has predefined values
  const hasFieldValues = (field: string) => {
    return Object.keys(FIELD_VALUE_OPTIONS).includes(field)
  }

  // Get field values for a specific field
  const getFieldValues = (field: string) => {
    return FIELD_VALUE_OPTIONS[field as keyof typeof FIELD_VALUE_OPTIONS] || []
  }

  // Handle field change in new condition
  const handleFieldChange = (value: string) => {
    setNewCondition({
      field: value,
      currentValue: undefined,
      beforeValue: undefined
    })
  }

  // Handle value change in new condition
  const handleBeforeValueChange = (value: string) => {
    setNewCondition((prev) => ({
      ...prev,
      beforeValue: value,
    }))
  }

    // Handle value change in new condition
    const handleValueChange = (value: string) => {
      setNewCondition((prev) => ({
        ...prev,
        currentValue: value,
      }))
    }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Label>
          <T tKey="operations.triggers.fields.conditions" />
        </Label>
        <Button type="button" variant="outline" size="sm" onClick={addCondition}>
          <Plus className="h-4 w-4 mr-1" />
          <T tKey="operations.triggers.conditions.add" />
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        <div>
          <Label htmlFor="newField">When Field Changes</Label>
          <Select value={newCondition.field} onValueChange={handleFieldChange}>
            <SelectTrigger id="newField">
              <SelectValue placeholder="Select field" />
            </SelectTrigger>
            <SelectContent>
              {availableFields.map((field) => (
                <SelectItem key={field.name} value={field.name}>
                  {field.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="newBeforeValue"> From (optional)</Label>
          {newCondition.field && hasFieldValues(newCondition.field) ? (
            <Select
              value={newCondition.beforeValue?.toString()}
              onValueChange={(value) => handleBeforeValueChange(value)}
            >
              <SelectTrigger id="newBeforeValue">
                <SelectValue placeholder="Select value" />
              </SelectTrigger>
              <SelectContent>
                {getFieldValues(newCondition.field).map((option) => (
                  <SelectItem key={option.value.toString()} value={option.value.toString()}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              id="newBeforeValue"
              value={newCondition.beforeValue?.toString()}
              onChange={(e) => handleBeforeValueChange(e.target.value)}
              placeholder="Value"
            />
          )}
        </div>

        <div>
          <Label htmlFor="newCurrentValue">To (mandatory)</Label>
          {newCondition.field && hasFieldValues(newCondition.field) ? (
            <Select
              value={newCondition.currentValue?.toString()}
              onValueChange={(value) => handleValueChange(value)}
            >
              <SelectTrigger id="newCurrentValue">
                <SelectValue placeholder="Select value" />
              </SelectTrigger>
              <SelectContent>
                {getFieldValues(newCondition.field).map((option) => (
                  <SelectItem key={option.value.toString()} value={option.value.toString()}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              id="newCurrentValue"
              value={newCondition.currentValue?.toString()}
              onChange={(e) => handleValueChange(e.target.value)}
              placeholder="Value"
            />
          )}
        </div>
      </div>

      {conditions.length === 0 ? (
        <div className="text-sm text-gray-500 italic text-center p-4 border rounded-md">
          <T tKey="operations.triggers.conditions.empty" />
        </div>
      ) : (
        <div className="space-y-3">
          {conditions.map((condition) => {
            const fieldLabel = availableFields.find((f) => f.name === condition.field)?.label || condition.field

            // Get the label for the value if it's a field with predefined values
            const valueLabel = condition.currentValue
            const beforeValueLabel = condition.beforeValue


            return (
              <div key={condition.id} className="flex items-center justify-between p-3 border rounded-md bg-gray-50">
                <div>
                When 
                  <span className="font-medium"> {fieldLabel} changes </span> 
                  {beforeValueLabel != undefined && <span>from {beforeValueLabel} </span>}
                  <span className="font-medium">to {valueLabel} </span>
                </div>
                <Button type="button" variant="ghost" size="icon" onClick={() => removeCondition(condition.id)}>
                  <X className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
